import React from 'react';
import Select, { components } from 'react-select';
import { CaretDownFill } from 'react-bootstrap-icons';
import Check from 'feather-icons-react/build/IconComponents/Check';

/**
 * SelectComponent — Reusable select dropdown for the entire admin panel.
 *
 * All colors are driven by CSS variables defined in ShopeasyUiKit.css,
 * so dark/light mode is handled automatically with zero per-file overrides.
 *
 * Props:
 *   id           – HTML id for the trigger button
 *   name         – field name (passed in onChange event)
 *   value        – current value (string | number | array for multiple)
 *   onChange     – handler: receives { target: { name, value } }
 *   options      – array of values, strings, or { value, label } objects
 *   placeholder  – placeholder text when nothing is selected
 *   required     – if true: no clearable, no "Clear Selection" entry
 *   className    – extra Tailwind classes to MERGE onto the trigger button
 *   label        – floating label text rendered above the trigger
 *   tabIndex     – HTML tabIndex for the trigger button
 *   disabled     – disables the component
 *   multiple     – enable multi-select mode (shows tag chips)
 *   searchable   – override auto-detect (auto shows search when options > 8)
 *   isClearable  – override auto-detect (auto-clearable if not required)
 *   loading      – shows a spinner and disables interaction
 *   error        – validation error string shown below the trigger
 *   children     – JSX <option> elements as an alternative to options prop
 */
const SelectComponent = ({
  id,
  name,
  value,
  onChange,
  options = [],
  placeholder = 'Select an option',
  required = false,
  className = '',
  label,
  tabIndex,
  disabled = false,
  multiple = false,
  searchable = true,
  loading = false,
  error = '',
  children,
  menuPortalTarget = typeof document !== 'undefined' ? document.body : null,
  size = 'md',
  small = false,
  medium = false,
  large = false,
  getOptionLabel: propGetOptionLabel,
  getOptionValue: propGetOptionValue,
  renderOption,
  formatOptionLabel: propFormatOptionLabel,
  filterOption: propFilterOption,
  filterOptions: propFilterOptions,
  isClearable,
  disableClearable,
  onBlur,
  onFocus,
  onInputChange,
  inputValue,
  minSearchLength = 0,
  noOptionsText,
  ...props
}) => {
  // Extract option objects from JSX <option> children recursively (fallback)
  const getOptionsFromChildren = (nodes) => {
    const result = [];
    React.Children.forEach(nodes, (child) => {
      if (!child) return;
      if (child.type === React.Fragment) {
        result.push(...getOptionsFromChildren(child.props.children));
      } else if (child.type === 'option') {
        result.push({ value: child.props.value, label: child.props.children, rawOpt: child.props.value });
      } else if (child.props && child.props.children) {
        result.push(...getOptionsFromChildren(child.props.children));
      }
    });
    return result;
  };

  const finalOptions =
    options && options.length > 0 ? options : getOptionsFromChildren(children);

  const getOptionDetails = (opt) => {
    if (opt === undefined || opt === null) return { value: '', label: '', rawOpt: opt };
    const isObject = typeof opt === 'object' && opt !== null;

    let optVal;
    if (propGetOptionValue && isObject) {
      optVal = propGetOptionValue(opt);
    } else if (isObject) {
      optVal = opt.value !== undefined
        ? opt.value
        : opt.template_name !== undefined
          ? opt.template_name
          : opt.template_key !== undefined
            ? opt.template_key
            : opt.id !== undefined
              ? opt.id
              : opt._id !== undefined
                ? opt._id
                : opt.key !== undefined
                  ? opt.key
                  : opt;
    } else {
      optVal = opt;
    }

    let optLabel;
    if (propGetOptionLabel && isObject) {
      optLabel = propGetOptionLabel(opt);
    } else if (isObject) {
      optLabel = opt.label || opt.template_name || opt.name || opt.title || opt.parent_category || opt.blogcategory_name || opt.tab_title || opt.brand_name || opt.product_name || opt.lowercase_category || opt.dial_code || opt.template_key || opt.key || optVal;
    } else {
      optLabel = opt;
    }

    if (typeof optVal === 'object' && optVal !== null) {
      optVal = optVal.value !== undefined
        ? optVal.value
        : optVal.template_name !== undefined
          ? optVal.template_name
          : optVal.template_key !== undefined
            ? optVal.template_key
            : optVal.name !== undefined
              ? optVal.name
              : optVal.title !== undefined
                ? optVal.title
                : optVal.id !== undefined
                  ? optVal.id
                  : optVal._id !== undefined
                    ? optVal._id
                    : optVal.key !== undefined
                      ? optVal.key
                      : '';
    }

    if (typeof optLabel === 'object' && optLabel !== null) {
      optLabel = optLabel.label !== undefined
        ? optLabel.label
        : optLabel.template_name !== undefined
          ? optLabel.template_name
          : optLabel.name !== undefined
            ? optLabel.name
            : optLabel.title !== undefined
              ? optLabel.title
              : optLabel.template_key !== undefined
                ? optLabel.template_key
                : optLabel.value !== undefined
                  ? optLabel.value
                  : optLabel.key !== undefined
                    ? optLabel.key
                    : '';
    }

    return {
      value: optVal !== undefined && optVal !== null && typeof optVal !== 'object' ? String(optVal) : '',
      label: optLabel !== undefined && optLabel !== null && typeof optLabel !== 'object' ? String(optLabel) : '',
      rawOpt: opt,
    };
  };

  const [optionCache, setOptionCache] = React.useState({});

  const parsedValueList = React.useMemo(() => {
    if (multiple) {
      const arr = Array.isArray(value) ? value : (value !== undefined && value !== null && value !== '' ? [value] : []);
      return arr.map(v => getOptionDetails(v)).filter(x => x.value !== undefined && x.value !== '');
    } else {
      if (value === undefined || value === null || value === '') return [];
      const details = getOptionDetails(value);
      if (details.value !== undefined && details.value !== '') {
        return [details];
      }
      return [];
    }
  }, [value, multiple, propGetOptionLabel, propGetOptionValue]);

  const rawBaseOptions = React.useMemo(() => {
    return finalOptions
      .map((opt) => {
        const { value: v, label: l, rawOpt: r } = getOptionDetails(opt);
        return { value: v, label: l, rawOpt: r };
      })
      .filter((opt) => !(opt.value === '' && opt.label === ''));
  }, [finalOptions, propGetOptionLabel, propGetOptionValue]);

  React.useEffect(() => {
    setOptionCache(prev => {
      const next = { ...prev };
      let changed = false;

      rawBaseOptions.forEach(opt => {
        if (opt.value !== undefined && opt.value !== '') {
          if (next[opt.value] !== opt.label) {
            next[opt.value] = opt.label;
            changed = true;
          }
        }
      });

      parsedValueList.forEach(opt => {
        if (opt.value !== undefined && opt.value !== '') {
          if (String(opt.label) !== String(opt.value)) {
            if (next[opt.value] !== opt.label) {
              next[opt.value] = opt.label;
              changed = true;
            }
          }
        }
      });

      return changed ? next : prev;
    });
  }, [rawBaseOptions, parsedValueList]);

  const selectOptions = React.useMemo(() => {
    const merged = [...rawBaseOptions];

    parsedValueList.forEach(pv => {
      const exists = merged.some(opt => String(opt.value) === String(pv.value));
      if (!exists) {
        const cachedLabel = optionCache[pv.value] || pv.label || pv.value;
        merged.push({ value: pv.value, label: cachedLabel, rawOpt: pv.rawOpt });
      }
    });

    return merged;
  }, [rawBaseOptions, parsedValueList, optionCache]);

  const getSelectedValue = () => {
    if (multiple) {
      const valArray = Array.isArray(value) ? value : (value !== undefined && value !== null && value !== '' ? [value] : []);
      const extractedVals = valArray.map(v => {
        const det = getOptionDetails(v);
        return String(det.value);
      });
      return selectOptions.filter((opt) =>
        extractedVals.includes(String(opt.value))
      );
    }
    const det = getOptionDetails(value);
    const found = selectOptions.find((opt) => String(opt.value) === String(det.value));
    if (found && (found.label !== '' || found.value !== '')) {
      return found;
    }
    return null;
  };

  const handleSelectChange = (selectedOption, actionMeta) => {
    let newValue;
    let rawSelected;

    if (multiple) {
      if (selectedOption) {
        newValue = selectedOption.map((opt) => opt.value);
        rawSelected = selectedOption.map((opt) => opt.rawOpt !== undefined ? opt.rawOpt : opt.value);
      } else {
        newValue = [];
        rawSelected = [];
      }
    } else {
      if (selectedOption) {
        newValue = selectedOption.value;
        rawSelected = selectedOption.rawOpt !== undefined ? selectedOption.rawOpt : selectedOption.value;
      } else {
        newValue = '';
        rawSelected = null;
      }
    }

    if (onChange) {
      // Standard FormInput synthetic event shape { target: { name, value } }
      // Also provide raw selected object for Autocomplete-like usages (e.g. onChange(e, selectedObject))
      const syntheticEvent = {
        target: {
          name: name || id || '',
          value: newValue,
        },
      };

      // Check if signature expects (event, value) like MUI Autocomplete or (event) like standard HTML select
      onChange(syntheticEvent, rawSelected);
    }
  };

  const handleInputChange = (val, actionMeta) => {
    if (onInputChange) {
      const syntheticEvent = { target: { value: val } };
      onInputChange(val, actionMeta, syntheticEvent);
    }
  };

  const customFilterOption = (option, rawInput) => {
    if (propFilterOption) {
      return propFilterOption(option, rawInput);
    }
    if (propFilterOptions) {
      const filtered = propFilterOptions(finalOptions, { inputValue: rawInput });
      return filtered.some((f) => {
        const det = getOptionDetails(f);
        return String(det.value) === String(option.value);
      });
    }

    if (minSearchLength > 0 && rawInput && rawInput.trim().length < minSearchLength) {
      return false;
    }
    if (!rawInput) {
      return true;
    }
    return String(option.label || '').toLowerCase().includes(rawInput.toLowerCase());
  };

  const noOptionsMessage = (inputObj) => {
    if (noOptionsText) {
      return typeof noOptionsText === 'function' ? noOptionsText(inputObj) : noOptionsText;
    }
    if (minSearchLength > 0 && inputObj && inputObj.inputValue && inputObj.inputValue.length < minSearchLength && !options.length) {
      return `Please enter at least ${minSearchLength} characters to search`;
    }
    return 'No options available';
  };

  const isSearchable = searchable !== null && searchable !== undefined ? searchable : true;
  const computedClearable = disableClearable ? false : (isClearable !== undefined ? isClearable : !required);

  // ── Determine Active Size ─────────────────────────────────────────────
  let resolvedSize = 'md';
  if (small || size === 'small' || size === 'sm') {
    resolvedSize = 'sm';
  } else if (large || size === 'large' || size === 'lg') {
    resolvedSize = 'lg';
  } else if (medium || size === 'medium' || size === 'md') {
    resolvedSize = 'md';
  }

  const sizeConfigs = {
    sm: {
      controlHeight: '30px',
      valueContainerHeight: '28px',
      fontSize: '12px',
      optionFontSize: '12px',
      optionPadding: '5px 10px',
      arrowIconClass: 'w-2.5 h-2.5',
      checkIconSize: 12,
      labelFontSize: '12px',
      borderRadius: '6px',
      indicatorPadding: '0 4px',
      multiValueFontSize: '11px',
      menuMaxHeight: '180px',
    },
    md: {
      controlHeight: '34px',
      valueContainerHeight: '32px',
      fontSize: '14px',
      optionFontSize: '13px',
      optionPadding: '8px 12px',
      arrowIconClass: 'w-3 h-3',
      checkIconSize: 14,
      labelFontSize: '13px',
      borderRadius: '8px',
      indicatorPadding: '0 8px',
      multiValueFontSize: '12px',
      menuMaxHeight: '220px',
    },
    lg: {
      controlHeight: '42px',
      valueContainerHeight: '40px',
      fontSize: '16px',
      optionFontSize: '15px',
      optionPadding: '10px 14px',
      arrowIconClass: 'w-3.5 h-3.5',
      checkIconSize: 16,
      labelFontSize: '14px',
      borderRadius: '8px',
      indicatorPadding: '0 10px',
      multiValueFontSize: '13px',
      menuMaxHeight: '260px',
    },
  };

  const currentSize = sizeConfigs[resolvedSize] || sizeConfigs.md;

  // Custom react-select styling to respect Shopeasy UI Kit CSS variables & size config
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: 'var(--secondary-bg-color)',
      borderColor: error
        ? 'var(--color-danger, #ef4444)'
        : state.isFocused
          ? 'var(--focus-border-color, #a3a3a6)'
          : 'var(--border-color, #e5e5e5)',
      color: 'var(--primary-text-color)',
      borderRadius: currentSize.borderRadius,
      fontSize: currentSize.fontSize,
      boxShadow: 'none',
      minHeight: currentSize.controlHeight,
      height: currentSize.controlHeight,
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      transition: 'all 150ms ease',
      cursor: 'pointer',
      '&:hover': {
        borderColor: error ? 'var(--color-danger, #ef4444)' : 'var(--focus-border-color, #a3a3a6)',
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 10px',
      height: currentSize.valueContainerHeight,
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'nowrap',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'var(--primary-text-color)',
      fontSize: currentSize.fontSize,
      margin: '0',
      lineHeight: '1',
      marginTop: '-2px',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'var(--table-text-color, #666b78)',
      opacity: 0.6,
      fontSize: currentSize.fontSize,
      margin: '0',
      lineHeight: '1',
      marginTop: '-2px',
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 99999,
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'var(--secondary-bg-color)',
      border: '1px solid var(--border-color, #e5e5e5)',
      borderRadius: currentSize.borderRadius,
      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
      zIndex: 99999,
      marginTop: '4px',
      padding: '4px',
    }),
    menuList: (provided) => ({
      ...provided,
      padding: '0',
      maxHeight: currentSize.menuMaxHeight,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected || state.isFocused
        ? 'var(--focus-tab-color, #f1f2f3)'
        : 'transparent',
      color: 'var(--primary-text-color)',
      cursor: 'pointer',
      fontSize: currentSize.optionFontSize,
      padding: currentSize.optionPadding,
      borderRadius: '6px',
      margin: '2px 0',
      transition: 'all 100ms ease',
      fontWeight: state.isSelected ? '500' : '400',
      '&:hover': {
        backgroundColor: 'var(--focus-tab-color, #f1f2f3)',
      },
      '&:active': {
        backgroundColor: 'var(--focus-tab-color, #f1f2f3)',
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: 'var(--focus-tab-color, #f1f2f3)',
      borderRadius: '4px',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: 'var(--primary-text-color)',
      fontSize: currentSize.multiValueFontSize,
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: 'var(--table-text-color, #666b78)',
      '&:hover': {
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        color: '#ef4444',
      },
    }),
    input: (provided) => ({
      ...provided,
      color: 'var(--primary-text-color)',
      fontSize: currentSize.fontSize,
      margin: '0',
      padding: '0',
      lineHeight: '1',
      marginTop: '-2px',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: currentSize.valueContainerHeight,
      alignItems: 'center',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: 'var(--table-text-color, #666b78)',
      padding: currentSize.indicatorPadding,
      '&:hover': {
        color: 'var(--primary-text-color)',
      },
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: 'var(--table-text-color, #666b78)',
      padding: '4px',
      cursor: 'pointer',
      '&:hover': {
        color: '#ef4444',
      },
    }),
  };

  const formatOptionLabel = (option, formatMeta) => {
    if (propFormatOptionLabel) {
      return propFormatOptionLabel(option.rawOpt !== undefined ? option.rawOpt : option, formatMeta);
    }
    if (renderOption) {
      return renderOption({ key: option.value }, option.rawOpt !== undefined ? option.rawOpt : option);
    }
    return option.label;
  };

  return (
    <div className="w-full flex flex-col text-left">
      {label && (
        <label
          htmlFor={id}
          className="select-label font-medium tracking-tight block mb-1"
          style={{
            color: 'var(--primary-text-color)',
            fontSize: currentSize.labelFontSize,
            marginBottom: '4px',
          }}
        >
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}

      <div className="relative w-full">
        <Select
          id={id}
          name={name}
          value={getSelectedValue()}
          onChange={handleSelectChange}
          options={selectOptions}
          placeholder={placeholder}
          isDisabled={disabled || loading}
          isMulti={multiple}
          isSearchable={isSearchable}
          isLoading={loading}
          isClearable={computedClearable}
          styles={customStyles}
          tabIndex={tabIndex}
          className={className}
          classNamePrefix="react-select"
          filterOption={customFilterOption}
          noOptionsMessage={noOptionsMessage}
          menuPortalTarget={menuPortalTarget}
          menuPosition="fixed"
          onBlur={onBlur}
          onFocus={onFocus}
          onInputChange={handleInputChange}
          {...(inputValue !== undefined ? { inputValue } : {})}
          formatOptionLabel={formatOptionLabel}
          components={{
            DropdownIndicator: (props) => (
              <components.DropdownIndicator {...props}>
                <CaretDownFill
                  className={`select-arrow-icon ${currentSize.arrowIconClass} transition-transform duration-200 ${props.selectProps.menuIsOpen ? 'rotate-180' : ''
                    }`}
                  style={{ color: 'var(--table-text-color, #666b78)' }}
                />
              </components.DropdownIndicator>
            ),
            Option: (props) => (
              <components.Option {...props}>
                <div className="flex items-center justify-between w-full">
                  <div className="flex-1 min-w-0">
                    {formatOptionLabel(props.data, { context: 'menu' })}
                  </div>
                  {props.isSelected && (
                    <Check
                      size={currentSize.checkIconSize}
                      className="text-green-600 ml-2 flex-shrink-0"
                      style={{ width: currentSize.checkIconSize, height: currentSize.checkIconSize }}
                    />
                  )}
                </div>
              </components.Option>
            ),
          }}
          {...props}
        />
      </div>

      {error && (
        <span className="text-red-500 text-xs font-medium tracking-tight mt-1">
          {error}
        </span>
      )}
    </div>
  );
};

export default SelectComponent;